"use client";

import React from "react";
import { Cpu, Shield, Wrench, Info, ArrowRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const tabsData = [{
  id: "engineering",
  label: "Достоверность"
}, {
  id: "safety",
  label: "Инклюзивность"
}, {
  id: "intelligence",
  label: "Экспертиза"
}];
const hotspotsData = [{
  id: "eng1",
  tabId: "engineering",
  x: 22,
  y: 55,
  title: "Проверка фактов",
  description: "Каждое утверждение проходит через двойную верификацию медицинскими экспертами и сертифицированными специалистами по велнесу."
}, {
  id: "eng2",
  tabId: "engineering",
  x: 82,
  y: 65,
  title: "Прозрачность источников",
  description: "Мы всегда указываем первоисточники исследований и научных публикаций, чтобы вы могли самостоятельно изучить детали."
}, {
  id: "saf1",
  tabId: "safety",
  x: 50,
  y: 45,
  title: "Безопасное пространство",
  description: "Наш контент создается с уважением ко всем идентичностям, обеспечивая комфортную среду для самопознания без осуждения."
}, {
  id: "saf2",
  tabId: "safety",
  x: 15,
  y: 65,
  title: "Этическая подача",
  description: "Мы используем деликатный язык и визуальные образы, которые вдохновляют, а не объективируют человеческое тело."
}, {
  id: "int1",
  tabId: "intelligence",
  x: 5,
  y: 50,
  title: "Медицинский совет",
  description: "Наши редакторы сотрудничают с практикующими врачами для обеспечения точности в вопросах сексуального здоровья."
}, {
  id: "int2",
  tabId: "intelligence",
  x: 35,
  y: 40,
  title: "Постоянное обновление",
  description: "Мы регулярно пересматриваем архивные материалы, чтобы они соответствовали последним научным открытиям и социальным нормам."
}];
export default function InnovationsAndTechnology() {
  return <section data-section-id="222043" id="editorial-standards" className="bg-secondary text-foreground py-20 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col items-center text-center mb-12 lg:mb-20">
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4 text-primary">
            Наши редакционные стандарты
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl font-sans">
            Мы верим, что качественная информация — это основа благополучия. Узнайте, как мы создаем контент, которому можно доверять.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <Tabs defaultValue="engineering" className="w-full flex flex-col items-center">
            <TabsList className="mb-8 grid w-full max-w-md grid-cols-3 bg-background border border-border">
              {tabsData.map((tab, i) => <TabsTrigger key={tab.id} value={tab.id} data-index={i} className="font-default data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  {tab.label}
                </TabsTrigger>)}
            </TabsList>

            <div className="w-full relative rounded-3xl overflow-hidden bg-background border border-border shadow-xl shadow-primary/5">
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000005_1px,transparent_1px),linear-gradient(to_bottom,#00000005_1px,transparent_1px)] bg-[size:24px_24px]" />
              
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />

              <div className="relative w-full aspect-[4/3] md:aspect-[21/9] flex items-center justify-center p-8">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654438/img/editorial_process_diagram.png" alt="Editorial Standards Diagram" className="w-full h-auto max-h-full object-contain opacity-90" />

                {tabsData.map((tab, tabIndex) => <TabsContent key={tab.id} value={tab.id} data-index={tabIndex} className="absolute inset-0 w-full h-full m-0 data-[state=inactive]:hidden">
                    {hotspotsData.filter(hotspot => hotspot.tabId === tab.id).map((hotspot, hotspotIndex) => <div key={hotspot.id} data-index={hotspotIndex} className="absolute z-10 -translate-x-1/2 -translate-y-1/2" style={{
                  left: `${hotspot.x}%`,
                  top: `${hotspot.y}%`
                }}>
                          <HoverCard>
                            <HoverCardTrigger asChild>
                              <Button variant="ghost" size="icon" className="group relative w-8 h-8 rounded-full bg-transparent hover:bg-transparent p-0 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background">
                                <span className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
                                <span className="relative flex items-center justify-center w-4 h-4 rounded-full bg-primary shadow-md transition-transform group-hover:scale-125">
                                  <span className="w-1.5 h-1.5 bg-white rounded-full" />
                                </span>
                              </Button>
                            </HoverCardTrigger>
                            <HoverCardContent className="w-80 bg-background/95 backdrop-blur-md border-border text-foreground shadow-2xl rounded-xl" sideOffset={10}>
                              <div className="flex justify-between space-x-4">
                                <div className="space-y-2">
                                  <h4 className="text-sm font-semibold font-default flex items-center gap-2 text-primary">
                                    <Info className="w-4 h-4" />
                                    {hotspot.title}
                                  </h4>
                                  <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                                    {hotspot.description}
                                  </p>
                                </div>
                              </div>
                            </HoverCardContent>
                          </HoverCard>
                        </div>)}
                  </TabsContent>)}
              </div>
            </div>
          </Tabs>

          <div className="mt-12 flex justify-center">
            <Button size="lg" className="rounded-full px-8 bg-primary text-primary-foreground hover:scale-105 transition-transform" asChild>
              <Link to="/AboutPage">
                Читать о нашей миссии
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}